import type {EditableCanvasControlDateRange} from "~/components/reports/canvasControls/canvasControlDateRange";
import type {EditableCanvasControlGranularity} from "~/components/reports/canvasControls/canvasControlGranularity";
import type {EditableCanvasControlMultiSelect} from "~/components/reports/canvasControls/canvasControlMultiSelect";
import type {EditableCanvasControlSelect} from "~/components/reports/canvasControls/canvasControlSelect";
import type {ComboChartSeriesDetails} from "~/components/reports/widgetSpecific/comboChart";
import type {EditableWidget} from "~/components/reports/widgetSpecific/widgets";
import type {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {ReportTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportTemplate";
import type {
    EratoColumnTypes,
    EratoDataTypes,
} from "~/modules/reports/domain/analyticsDatasetTypes";
import type {ReportPage} from "~/domainModels/reports/reportPage";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EnumDictionary} from "~/typeDefinitions";
import {CanvasControlType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";

export enum DragTypes {
    moving = "f1642f79-1ae6-4c0d-8857-998d8aac41f6",
    resizing = "0943b2d7-229c-4eda-a6e2-1942ef1c6eeb",
    newWidget = "807f6c49-7c2e-477d-b8a1-f35c6dd01d8c",
    newPageTemplate = "fa3813ed-53e0-4bca-9854-14ebf45ef42e",
    newWidgetTemplate = "e177b9e0-f45e-4ed7-bd18-c2fe005d8c2c",
}

export enum ResizeHandlePosition {
    east = "1737c9f1-0133-4b4d-bbb4-22ebeba3647f",
    west = "464d198a-c516-4ce9-b918-7eec0f3ece0b",
    north = "0e9d509d-ff31-4240-ae74-b5d1af03da49",
    south = "3b985b4f-0fc7-48f8-9da8-aafd8b6605e9",
    northWest = "7438f07d-cb11-4ef5-8cce-8f91001cff47",
    northEast = "7bab7fbf-d9fc-476f-9cef-b85022669fdc",
    southWest = "66764d4a-7235-4b71-b1cd-db16800c4dc2",
    southEast = "22e4be23-6f8a-465e-8ca0-042cc8b6b238",
}

export type DndDraggable =
    | {
          index: number;
          rowStart: number;
          rowEnd: number;
          colStart: number;
          colEnd: number;
          isNew?: boolean;
          pageIndex: number;
          dndType: DragTypes.moving;
      }
    | {
          index: number;
          rowStart: number;
          rowEnd: number;
          colStart: number;
          colEnd: number;
          resizeHandlePosition: ResizeHandlePosition | null;
          pageIndex: number;
          dndType: DragTypes.resizing;
      }
    | {
          dndType: DragTypes.newPageTemplate;
          reportTemplate: ReportTemplate;
      }
    | {
          dndType: DragTypes.newWidget;
          widgetType: WidgetType;
          isNew: true;
          widgetHeightInRows: number;
          widgetWidthInCols: number;
      }
    | {
          dndType: DragTypes.newWidgetTemplate;
          widgetConfiguration: any;
      };

export type DndGridOccupancyMatrix = Array<Array<number | null>>;

export enum QueryResolverErrorCodes {
    notSignedIn = "9fc6d95a-9d78-4da1-a378-91b9a71a6863",
    nullInputs = "19d16821-7e4e-4762-9172-ac207552b860",
    noMetrics = "93973152-d20d-403a-b0c5-8e8826d8b7aa",
    noDimensions = "956dd210-0855-4f00-9941-a0b75c6d4928",
    noDataSourceFound = "a818d3a0-026f-4209-9539-ca14e25d9caa",
    noDataFound = "7d30b96f-1863-4af0-86a0-0b06e4f1d719",
    metricsMismatch = "f0fdff29-7a27-4a55-b402-4ad83aed8445",
    dimensionsMismatch = "de472543-837d-460d-ab2b-eb602998fc5f",
    noMetricsAndDimensions = "15a1a38d-1c2d-4b16-b3d4-d12f54259b1c",
    noCompanyDatabaseFound = "24b9d164-e67e-4701-a435-d35c5f8f8283",
    noTable = "6dd1d628-849a-4e9f-b977-3eddba11282b",
    incompatibleMetricsAndDimensions = "cb275bc7-d130-458e-bbf8-1958ceccea04",
    invalidIntegrationType = "c9b98c19-993f-4a6f-b023-bb5e2ecaff5d",
    invalidOperation = "b82fc459-61ce-4815-8177-57ffcbb78233",
    hadesError = "b9c7763e-1af7-4b3b-94ab-765649769791",
}

export enum Popups {
    cancelEditing = "cancelEditing",
    duplicatePage = "duplicatePage",
    useTemplate = "useTemplate",
    duplicateReport = "duplicateReport",
    deleteReport = "deleteReport",
    destructiveWidgetTypeChange = "destructiveWidgetTypeChange",
    clearPage = "clearPage",
    deletePage = "deletePage",
    deleteWidget = "deleteWidget",
}

export enum TemplatePlacement {
    top = "369befdf-177a-4ed5-85bc-6794b57c9ce1",
    bottom = "9b4c5455-94d6-4f3c-98fd-1e5cdda5a555",
}

export type EditableCanvasControl =
    // | EditableCanvasControlString
    // | EditableCanvasControlNumber
    // | EditableCanvasControlDate
    // | EditableCanvasControlBoolean
    EditableCanvasControlGranularity | EditableCanvasControlDateRange | EditableCanvasControlSelect | EditableCanvasControlMultiSelect;

export const canvasControlsDataTypesMetadata: EnumDictionary<CanvasControlType, {humanReadableName: string}> = {
    // [CanvasControlType.string]: {
    //     humanReadableName: "String",
    // },
    // [CanvasControlType.date]: {
    //     humanReadableName: "Date",
    // },
    // [CanvasControlType.number]: {
    //     humanReadableName: "Number",
    // },
    // [CanvasControlType.boolean]: {
    //     humanReadableName: "Boolean",
    // },
    [CanvasControlType.granularity]: {
        humanReadableName: "Granularity",
    },
    [CanvasControlType.dateRange]: {
        humanReadableName: "DateRange",
    },
    [CanvasControlType.select]: {
        humanReadableName: "Select",
    },
    [CanvasControlType.multiSelect]: {
        humanReadableName: "MultiSelect",
    },
};

export enum FilterOperations {
    equals = "9bf4a803-3c97-4ede-8226-2f286e4bad33",
}

export type WidgetFrontendFilters = {
    [filterProperty: string]: {
        attributeId: Uuid;
        [FilterOperations.equals]: Array<string>;
    };
};

export type WidgetSorts = {
    [sortColumnKey: string]: ColumnSortType | null;
};

export enum ColumnSortType {
    ascending = "a6993884-dd24-4924-8bd6-4eb3e27371ab",
    descending = "6c22f5c4-4eb3-4d13-9d7b-0f5d88c63d6a",
}

export enum Granularity {
    day = "day",
    month = "month",
    year = "year",
}

export enum TableColumnDataTypes {
    string = "f01fb873-f89f-476b-b1df-ab1e4bbd21d2",
    numeric = "0253f346-f6aa-4afa-85ba-a14811a5b0f3",
}

export const tableColumnDataTypes: EnumDictionary<TableColumnDataTypes, {humanReadableName: string}> = {
    [TableColumnDataTypes.string]: {
        humanReadableName: "String",
    },
    [TableColumnDataTypes.numeric]: {
        humanReadableName: "Numeric",
    },
};

export enum TableColumnTextOverflow {
    wrap = "d61faa0d-2fdd-48d8-bef9-8016cad647f9",
    truncate = "58b588bd-1bab-4601-9841-b06e6effcccd",
}

export const tableColumnTextOverflow: EnumDictionary<TableColumnTextOverflow, {humanReadableName: string}> = {
    [TableColumnTextOverflow.wrap]: {
        humanReadableName: "Wrap",
    },
    [TableColumnTextOverflow.truncate]: {
        humanReadableName: "Truncate",
    },
};

export type ColumnProperties = {
    columnWidth: number;
    columnTitle: string;
    columnDataType: TableColumnDataTypes;
    columnTextOverflow: TableColumnTextOverflow;
    isColumnWidthEdited: boolean;
};

export enum WidgetValueType {
    intrinsic = "77adefb5-2f53-4151-a1df-11de211acb93",
    canvasControl = "6e2b2115-6b76-47d6-8540-181bdea6a2b4",
}

export type WidgetIntrinsicGranularity = {
    type: WidgetValueType.intrinsic;
    value: Granularity;
};

export type WidgetCanvasControlGranularity = {
    type: WidgetValueType.canvasControl;
    value: Uuid;
};

export type GranularityForWidget = WidgetIntrinsicGranularity | WidgetCanvasControlGranularity;

export type DraggingGhostProps = {
    cellIndex: number;
    rowSpan: number;
    colSpan: number;
    origin: {
        row: number;
        col: number;
    };
    pageIndex: number;
};

export enum DateRangeOptions {
    // allTime = "bf0a9ef6-25a9-434b-9e85-bf8815160f8d",
    today = "c302b22c-5097-442a-8ab6-917ca1eae4fa",
    yesterday = "fc927bbc-ddbf-4d01-bf22-f24146941645",
    thisWeek = "e3d40518-6ad9-47c1-98d5-e3c832807ea0",
    lastWeek = "68cfa38e-0ce0-4a5e-84e1-94943cf7bcd6",
    lastSevenDays = "ea2c02a0-3dfe-4fb9-940b-c91cacc00c63",
    lastFourteenDays = "c80cd0cb-df06-40a6-b891-db1ff29f71ba",
    monthToDate = "c7f19cee-b41b-4697-a8e2-38c64fe32475",
    lastMonth = "f2355cfd-610a-4a3b-ae43-0ae6b9c5a537",
    lastMonthToDate = "f1113080-f8c2-46db-aaec-fd33f4d4ca3e",
    lastThirtyDays = "188731e9-fb42-4b9b-a03b-f9fd7ee9ab66",
    quarterToDate = "2d444c55-07ef-4162-97b8-6a2201f9be03",
    lastQuarter = "14152cf9-09f9-4b38-b0d0-1df6de3c826d",
    lastQuarterToDate = "af5bfb01-c1a9-46c8-9f86-11a717ac3d63",
    yearToDate = "8597452d-207e-4983-b0a6-8b6e379e29e2",
    lastYear = "58412a34-4543-479d-b278-43d31acdf10e",
    lastYearToDate = "2265a1c5-fd48-4674-8475-ecff282de975",
}

export type WidgetMetric = {
    id: Uuid;
    displayName: string;
    // Please kill Saurav for this
    type: EratoDataTypes | "text";
};

export type WidgetMetrics = {
    [id: Uuid]: WidgetMetric;
};

export type WidgetDimensions = {
    [id: Uuid]: WidgetDimension;
};

export type WidgetDimension = {
    id: Uuid;
    displayName: string;
    // Please kill Saurav for this
    type: EratoDataTypes | "text";
};

export type EditableReportPage = ReportPage & {
    widgets: Array<EditableWidget>;
    // TODO: Convert to array
    gridCellToOccupancy: DndGridOccupancyMatrix;
    isPageNameEditing: boolean;
};

export type SecondaryAxisDetails = {
    selectedMetrics: Array<Uuid>;
};

export type SecondaryAxisDetailsComboChart = {
    chartSeriesDetails: Array<ComboChartSeriesDetails>;
};

export type GetFilterValuesForAttributeActionData =
    | {
          success: false;
          error: string;
      }
    | {
          success: true;
          data:
              | {
                    type: EratoColumnTypes.metric;
                    minValue: number;
                    maxValue: number;
                    filterId: Uuid;
                }
              | {
                    type: EratoColumnTypes.dimension;
                    dataType: EratoDataTypes.string;
                    possibleValues: Array<string>;
                    filterId: Uuid;
                };
      };
